import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { RealtimeProvider } from '@/components/layout/RealtimeProvider';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToastProvider } from '@/components/shared/Toast';
import { LoginPage } from '@/components/auth/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { UsersPage } from '@/pages/UsersPage';
import { PoliciesPage } from '@/pages/PoliciesPage';
import { JobsPage } from '@/pages/JobsPage';
import { ServicesPage } from '@/pages/ServicesPage';
import { MeetingsPage } from '@/pages/MeetingsPage';
import { PlanningPage } from '@/pages/PlanningPage';
import { RepositoriesPage } from '@/pages/RepositoriesPage';
import { RepositoryDetailPage } from '@/pages/repositories/RepositoryDetailPage';
import { PullRequestsPage } from '@/pages/repositories/PullRequestsPage';
import { BranchesPage } from '@/pages/BranchesPage';
import { DaytonaPage } from '@/pages/DaytonaPage';
import { StorageSourcesPage } from '@/pages/storage/StorageSourcesPage';
import { StorageAccountsPage } from '@/pages/storage/StorageAccountsPage';
import { StorageCollectionsPage } from '@/pages/storage/StorageCollectionsPage';
import { StorageFilesPage } from '@/pages/storage/StorageFilesPage';
import { MarkdownEditorPage } from '@/pages/MarkdownEditorPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { WebhooksPage } from '@/pages/webhooks/WebhooksPage';
import { WebhookDetailPage } from '@/pages/webhooks/WebhookDetailPage';
import { ProvidersPage } from '@/pages/agents/ProvidersPage';
import { ModelsPage } from '@/pages/agents/ModelsPage';
import { ModelCollectionsPage } from '@/pages/agents/ModelCollectionsPage';
import { FactoriesPage } from '@/pages/agents/FactoriesPage';
import { FactoryDetailPage } from '@/pages/agents/FactoryDetailPage';
import { ConversationsPage } from '@/pages/agents/ConversationsPage';
import { ConversationDetailPage } from '@/pages/agents/ConversationDetailPage';
import { OrganizationsPage } from '@/pages/organizations/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/organizations/OrganizationDetailPage';
import { UserGroupsPage } from '@/pages/UserGroupsPage';
import { RolesPage } from '@/pages/RolesPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ProjectDetailPage } from '@/pages/projects/ProjectDetailPage';
import { PlanningBoardPage } from '@/pages/projects/PlanningBoardPage';
import { ApprovalsPage } from '@/pages/ApprovalsPage';
import { SecretsPage } from '@/pages/SecretsPage';
import { IndexerPage } from '@/pages/IndexerPage';
import { VoicesPage } from '@/pages/VoicesPage';
import { BulkOperationsPage } from '@/pages/BulkOperationsPage';
import { StoresPage } from '@/pages/StoresPage';
import { WorkspacesPage } from '@/pages/WorkspacesPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { MailboxesPage } from '@/pages/mailboxes/MailboxesPage';
import { SharesPage } from '@/pages/SharesPage';
import { SimpleRolesPage } from '@/pages/SimpleRolesPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { DiscountsPage } from '@/pages/DiscountsPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { PaymentsPage } from '@/pages/PaymentsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { PosRoomsPage } from '@/pages/PosRoomsPage';
import { CinemaPage } from '@/pages/CinemaPage';
import { CINEMA_ROUTE_PATH } from '@/pages/CinemaShared';
import { KioskTemplatesPage } from '@/pages/kiosks/KioskTemplatesPage';
import { KioskTemplateBuilderPage } from '@/pages/kiosks/KioskTemplateBuilderPage';
import { KioskDevicesPage } from '@/pages/kiosks/KioskDevicesPage';

// Lazy-loaded so Monaco + the heavy editor only load when the form is opened.
const WebhookFormPage = lazy(() =>
  import('@/pages/webhooks/WebhookFormPage').then((m) => ({ default: m.WebhookFormPage })),
);
const FactoryFormPage = lazy(() =>
  import('@/pages/agents/FactoryFormPage').then((m) => ({ default: m.FactoryFormPage })),
);
const PolicyBuilderPage = lazy(() =>
  import('@/pages/PolicyBuilderPage').then((m) => ({ default: m.PolicyBuilderPage })),
);

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          element={
            <AuthGuard>
              <RealtimeProvider>
                <AppLayout />
              </RealtimeProvider>
            </AuthGuard>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/planning" element={<PlanningPage />} />
          <Route path="/repositories" element={<RepositoriesPage />} />
          <Route path="/repositories/pull-requests" element={<PullRequestsPage />} />
          <Route path="/repositories/:repoId/branches" element={<BranchesPage />} />
          <Route path="/repositories/:repoId" element={<RepositoryDetailPage />} />
          <Route path="/daytona" element={<DaytonaPage />} />
          <Route path="/instances" element={<Navigate to="/daytona" replace />} />
          <Route path="/storage" element={<Navigate to="/storage/collections" replace />} />
          <Route path="/storage/sources" element={<StorageSourcesPage />} />
          <Route path="/storage/sources/:sourceId/accounts" element={<StorageAccountsPage />} />
          <Route path="/storage/collections" element={<StorageCollectionsPage />} />
          <Route path="/storage/collections/:collectionId/files" element={<StorageFilesPage />} />
          <Route path="/storage/files/:fileId/markdown" element={<MarkdownEditorPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/webhooks" element={<WebhooksPage />} />
          <Route
            path="/webhooks/new"
            element={
              <Suspense fallback={<div />}>
                <WebhookFormPage />
              </Suspense>
            }
          />
          <Route path="/webhooks/:webhookId" element={<WebhookDetailPage />} />
          <Route
            path="/webhooks/:webhookId/edit"
            element={
              <Suspense fallback={<div />}>
                <WebhookFormPage />
              </Suspense>
            }
          />

          {/* ── Epic-6 Agent platform pages (6.2) ───────────────────────── */}
          <Route path="/providers" element={<ProvidersPage />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/model-collections" element={<ModelCollectionsPage />} />
          <Route path="/factories" element={<FactoriesPage />} />
          <Route
            path="/factories/new"
            element={
              <Suspense fallback={<div />}>
                <FactoryFormPage />
              </Suspense>
            }
          />
          <Route path="/factories/:factoryId" element={<FactoryDetailPage />} />
          <Route
            path="/factories/:factoryId/edit"
            element={
              <Suspense fallback={<div />}>
                <FactoryFormPage />
              </Suspense>
            }
          />
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route path="/conversations/:conversationId" element={<ConversationDetailPage />} />

          {/* ── Epic-6 foundation routes (pages land in O6-C/D/E) ────────── */}
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/projects/:projectId/board" element={<PlanningBoardPage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/organizations/:orgId" element={<OrganizationDetailPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/user-groups" element={<UserGroupsPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/secrets" element={<SecretsPage />} />
          <Route path="/mailboxes" element={<MailboxesPage />} />
          <Route path="/bulk-operations" element={<BulkOperationsPage />} />
          <Route path="/indexer" element={<IndexerPage />} />
          <Route path="/voices" element={<VoicesPage />} />
          <Route path="/stores" element={<StoresPage />} />
          {/* One optional-param route per resource: the pages derive
              createMode from `<param> === 'new'`. A separate literal
              "/x/new" route has no param, so createMode stayed false and the
              list re-rendered instead of the create form. */}
          <Route path="/stores/:storeId?" element={<StoresPage />} />
          <Route path="/stores/:storeId/roles" element={<SimpleRolesPage />} />
          <Route path="/stores/:storeId/roles/:roleId" element={<SimpleRolesPage />} />
          <Route path="/workspaces/:workspaceId" element={<WorkspacesPage />} />
          <Route path="/customers/:customerId?" element={<CustomersPage />} />
          <Route path="/shares/:shareId?" element={<SharesPage />} />
          <Route path="/catalog/:productId?" element={<CatalogPage />} />
          <Route path="/discounts/:discountId?" element={<DiscountsPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrdersPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/pos-rooms" element={<PosRoomsPage />} />
          <Route path="/pos-rooms/:roomId" element={<PosRoomsPage />} />
          {/* A single optional-param route covers list, create ("/new"), and detail.
              The Cinema pages derive createMode from `id === 'new'`, so a route
              without the `:id` param silently rendered the list on Create. */}
          <Route path={CINEMA_ROUTE_PATH} element={<CinemaPage />} />
          {/* Kiosk template builder + device management (P4 T06) */}
          <Route path="/kiosks/templates" element={<KioskTemplatesPage />} />
          <Route path="/kiosks/templates/:templateId" element={<KioskTemplateBuilderPage />} />
          <Route path="/kiosks/devices" element={<KioskDevicesPage />} />
          {/* /kiosks/devices/new is captured by :deviceId → in-page register form */}
          <Route path="/kiosks/devices/:deviceId" element={<KioskDevicesPage />} />
          <Route
            path="/policies/builder"
            element={
              <Suspense fallback={<div />}>
                <PolicyBuilderPage />
              </Suspense>
            }
          />
        </Route>

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
